<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BuzzReportSection from './BuzzReportSection.vue';

const props = defineProps({
  pulsing: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const blockClass = computed(() =>
  props.pulsing ? 'animate-pulse bg-n-alpha-1' : 'bg-n-alpha-1'
);

const cardShellClass =
  'm-0.5 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 overflow-hidden';
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid gap-3 lg:grid-cols-6 lg:items-stretch">
      <div class="flex flex-col gap-3 lg:col-span-2">
        <div
          class="relative"
          :class="[cardShellClass, { 'animate-pulse': pulsing }]"
        >
          <div class="absolute inset-x-0 bottom-0 h-10 bg-n-alpha-1/60" />
          <div
            class="relative z-10 flex items-start justify-between gap-3 px-4 py-2.5"
          >
            <div class="flex flex-col gap-1">
              <div class="h-3 w-16 rounded bg-n-alpha-1" />
              <div class="h-8 w-12 rounded bg-n-alpha-1" />
            </div>
            <div class="h-3 w-16 rounded bg-n-alpha-1" />
          </div>
          <p
            v-if="message"
            class="relative z-10 px-4 pb-2.5 text-xs text-n-slate-10 mb-0"
          >
            {{ message }}
          </p>
        </div>

        <div
          class="flex flex-col"
          :class="[cardShellClass, { 'animate-pulse': pulsing }]"
        >
          <div class="px-4 pt-2.5 pb-1">
            <div class="h-3 w-24 rounded bg-n-alpha-1" />
          </div>
          <div class="flex flex-col gap-1 px-4 pb-2.5">
            <div
              v-for="index in 3"
              :key="`formula-line-${index}`"
              class="flex justify-between gap-2"
            >
              <div class="h-3 w-14 rounded bg-n-alpha-1" />
              <div class="flex gap-1.5">
                <div class="h-3 w-6 rounded bg-n-alpha-1" />
                <div class="h-3 w-7 rounded bg-n-alpha-1" />
              </div>
            </div>
            <div
              class="border-t border-n-container pt-1.5 flex justify-between gap-2"
            >
              <div class="h-3 w-16 rounded bg-n-alpha-1" />
              <div class="h-3 w-7 rounded bg-n-alpha-1" />
            </div>
          </div>
        </div>
      </div>

      <div
        class="relative min-h-52 lg:col-span-4 lg:min-h-0"
        :class="[cardShellClass, { 'animate-pulse': pulsing }]"
      >
        <div
          class="flex h-full min-h-52 flex-col lg:absolute lg:inset-0 lg:min-h-0 print:relative print:inset-auto print:min-h-52"
        >
          <div class="px-4 pt-2.5 pb-1 shrink-0">
            <div class="h-3 w-20 rounded bg-n-alpha-1" />
          </div>
          <div
            class="flex-1 min-h-0 flex items-center justify-center px-2 pb-2"
          >
            <svg
              class="w-32 h-32 text-n-alpha-1"
              viewBox="0 0 200 200"
              aria-hidden="true"
            >
              <polygon
                fill="none"
                stroke="currentColor"
                stroke-width="1"
                points="100,20 170,75 145,160 55,160 30,75"
              />
              <polygon
                fill="currentColor"
                fill-opacity="0.35"
                stroke="currentColor"
                stroke-width="2"
                points="100,45 145,80 130,135 70,135 55,80"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <BuzzReportSection :header="t('BUZZ_REPORTS.SCALE.TITLE')">
      <div class="space-y-3 py-2">
        <div
          class="h-3 w-full rounded-full bg-n-alpha-2"
          :class="{ 'animate-pulse': pulsing }"
        />
        <div
          class="h-3 w-1/2 rounded bg-n-alpha-2"
          :class="{ 'animate-pulse': pulsing }"
        />
      </div>
    </BuzzReportSection>

    <BuzzReportSection :header="t('BUZZ_REPORTS.DISTRIBUTION.TITLE')">
      <div class="flex items-end gap-2 h-48">
        <div
          v-for="index in 5"
          :key="`distribution-${index}`"
          class="flex-1 rounded-t-md"
          :class="blockClass"
          :style="{ height: `${30 + index * 12}%` }"
        />
      </div>
    </BuzzReportSection>

    <BuzzReportSection :header="t('BUZZ_REPORTS.CATEGORY_REVIEWS')">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div
          v-for="index in 2"
          :key="`reviews-${index}`"
          class="rounded-lg p-3 space-y-2"
          :class="blockClass"
        >
          <div class="h-3 w-2/3 rounded bg-n-alpha-1" />
          <div class="h-6 w-1/3 rounded bg-n-alpha-1" />
        </div>
      </div>
    </BuzzReportSection>

    <BuzzReportSection :header="t('BUZZ_REPORTS.CATEGORY_MEDIA')">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div
          v-for="index in 2"
          :key="`media-${index}`"
          class="rounded-lg p-3 space-y-2"
          :class="blockClass"
        >
          <div class="h-3 w-2/3 rounded bg-n-alpha-1" />
          <div class="h-6 w-1/3 rounded bg-n-alpha-1" />
        </div>
      </div>
      <div class="border-t border-n-container pt-4 space-y-3">
        <div class="h-4 w-24 rounded" :class="blockClass" />
        <div
          v-for="index in 3"
          :key="`mentions-${index}`"
          class="grid grid-cols-4 gap-3"
        >
          <div class="h-3 rounded col-span-1" :class="blockClass" />
          <div class="h-3 rounded col-span-2" :class="blockClass" />
          <div class="h-3 rounded col-span-1" :class="blockClass" />
        </div>
      </div>
    </BuzzReportSection>

    <BuzzReportSection :header="t('BUZZ_REPORTS.CATEGORY_FOOTFALL')">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div
          v-for="index in 2"
          :key="`footfall-${index}`"
          class="rounded-lg p-3 space-y-2"
          :class="blockClass"
        >
          <div class="h-3 w-2/3 rounded bg-n-alpha-1" />
          <div class="h-6 w-1/3 rounded bg-n-alpha-1" />
        </div>
      </div>
      <div class="flex items-end gap-2 h-48">
        <div
          v-for="index in 7"
          :key="`histogram-${index}`"
          class="flex-1 rounded-t-md"
          :class="blockClass"
          :style="{ height: `${25 + (index % 4) * 15}%` }"
        />
      </div>
    </BuzzReportSection>

    <BuzzReportSection :header="t('BUZZ_REPORTS.HISTORY')">
      <div
        class="h-72 rounded-lg border border-dashed border-n-container bg-n-alpha-2/40 flex items-center justify-center px-6"
        :class="{ 'animate-pulse': pulsing }"
      >
        <svg
          class="w-full h-40 text-n-alpha-2"
          viewBox="0 0 320 120"
          aria-hidden="true"
        >
          <polyline
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            points="0,90 40,70 80,75 120,45 160,55 200,30 240,40 280,20 320,35"
          />
          <circle cx="0" cy="90" r="4" class="fill-current" />
          <circle cx="40" cy="70" r="4" class="fill-current" />
          <circle cx="80" cy="75" r="4" class="fill-current" />
          <circle cx="120" cy="45" r="4" class="fill-current" />
          <circle cx="160" cy="55" r="4" class="fill-current" />
          <circle cx="200" cy="30" r="4" class="fill-current" />
          <circle cx="240" cy="40" r="4" class="fill-current" />
          <circle cx="280" cy="20" r="4" class="fill-current" />
          <circle cx="320" cy="35" r="4" class="fill-current" />
        </svg>
      </div>
    </BuzzReportSection>
  </div>
</template>
